import React, { useState } from 'react'
import styled from 'styled-components'
import { spacing } from 'sutra/tokens/spacing'

// bully ignore[no-cross-feature-private-imports: FilterControl is the panel BODY ControlsSidebar itself mounts; ControlsSidebar's collapsible-sidebar chrome doesn't fit a 640px inline form, and the charts-v2 barrel only value-exports ControlsSidebar (FilterControlProps is type-only). Same seam KeywordsFilterSidebar.tsx uses for ControlsSidebar. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { FilterControl } from 'features/charts-v2/components/FilterControl'
// bully ignore[no-cross-feature-private-imports: same established seam as FilterControl above — FilterCardsSkeleton is the canonical filter-card loading placeholder charts-v2 already ships (FilterControl's own isFilterOptionsLoading branch), reused here for the LLM-suggest loading window rather than a second hand-rolled shimmer. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { FilterCardsSkeleton } from 'features/charts-v2/components/skeletons'
import type { UseAgentFilterPanelResult } from 'src/hooks/use-agent-filter-panel'

// RatingTypePopover / SentimentFilterCard resolve this host by id and
// render nothing when it's absent (ControlsSidebar.tsx is where every chart
// surface gets it for free) — this panel keeps the same id for them.
// FilterPopover/FilterOperatorPopover portal to document.body and don't use it.
//
// Mount unconditionally. On an agent-type swap (Issue ↔ Benchmarking Fields
// render in the same slot), the incoming panel's render runs BEFORE the
// outgoing panel's host is removed at commit, so a "does a host already
// exist" check sees a stale host and skips mounting. Commit then deletes the
// stale host, leaving none — every popover under the new panel silently
// renders nothing. Mount unconditionally and pass the node directly (see
// `filterPopoverPortalTarget` below) to avoid the race.
const POPOVER_PORTAL_CONTAINER_ID = 'popover-portal-container'

// Placeholder count for the suggest-in-flight skeleton — actual count isn't
// known until the LLM call resolves.
const SUGGESTING_SKELETON_COUNT = 3

export interface AgentFilterPanelProps {
  filterControlProps: UseAgentFilterPanelResult['filterControlProps']
  /** Left of the Filters header row. Node (not string) so callers keep their
   *  own typography. */
  headerLabel: React.ReactNode
  /** Forwarded to FilterControl → FilterSelectHeader. */
  emptyStateLabel?: string
  /** True while the LLM suggest call is in flight — swaps cards for a skeleton. */
  isSuggesting?: boolean
  /** Slot rendered below the filter cards.
   *  Slot (not a boolean) so the container owns the data source. */
  staleFiltersNotice?: React.ReactNode
}

/**
 * Mounts the charts-v2 filter-card list + add-filter picker inline (no
 * `ControlsSidebar` chrome). Also owns the Filters header row, using the
 * same `headerActionsPortalTarget` mechanism as `ControlsSidebar` to host
 * FilterSelectHeader's `[+]` trigger.
 */
export const AgentFilterPanel: React.FC<AgentFilterPanelProps> = ({
  filterControlProps,
  headerLabel,
  emptyStateLabel,
  isSuggesting = false,
  staleFiltersNotice,
}) => {
  const [popoverHost, setPopoverHost] = useState<HTMLDivElement | null>(null)
  const [headerActionsSlot, setHeaderActionsSlot] =
    useState<HTMLDivElement | null>(null)

  return (
    <div>
      <div id={POPOVER_PORTAL_CONTAINER_ID} ref={setPopoverHost} />
      <FiltersHeaderRow>
        {headerLabel}
        <HeaderActionsSlot ref={setHeaderActionsSlot} />
      </FiltersHeaderRow>
      {isSuggesting ? (
        <FilterCardsSkeleton count={SUGGESTING_SKELETON_COUNT} />
      ) : (
        <FilterControl
          {...filterControlProps}
          headerActionsPortalTarget={headerActionsSlot}
          filterPopoverPortalTarget={popoverHost}
          emptyStateLabel={emptyStateLabel}
        />
      )}
      {!isSuggesting && staleFiltersNotice}
    </div>
  )
}

const FiltersHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[2]}px;
`

const HeaderActionsSlot = styled.div`
  display: flex;
  align-items: center;
`
